import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, LogOut, LayoutDashboard, Menu, X, Globe, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang, LANGS } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";

export const Navbar = ({ onSearch, search }) => {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  const link = (to, label, testid) => {
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`relative text-sm tracking-wide font-medium transition-colors py-1 whitespace-nowrap flex-shrink-0 ${
          isActive ? "text-white font-semibold" : "text-zinc-400 hover:text-white"
        }`}
      >
        {label}
        {isActive && (
          <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#E11D48] rounded-full" />
        )}
      </Link>
    );
  };

  const LangSwitcher = () => (
    <div className="flex items-center gap-1 border border-white/10 rounded-lg px-2 py-1 bg-black/40 flex-shrink-0 whitespace-nowrap" data-testid="lang-switcher">
      <Globe size={13} className="text-zinc-400 ml-0.5 flex-shrink-0" />
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-${l.code}`}
          onClick={() => setLang(l.code)}
          aria-label={`Cambiar idioma a ${l.label}`}
          className={`px-2 py-0.5 text-[11px] rounded-md font-medium transition-all whitespace-nowrap ${
            lang === l.code ? "bg-[#E11D48] text-white font-semibold shadow-sm" : "text-zinc-400 hover:text-white"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-40 px-4 sm:px-6 md:px-16 py-3.5 transition-all duration-300 ${
        scrolled
          ? "bg-black/90 backdrop-blur-md shadow-lg shadow-black/50 border-b border-white/5"
          : "bg-gradient-to-b from-black/95 via-black/60 to-transparent"
      }`}
    >
      <div className="flex items-center justify-between max-w-7xl mx-auto gap-4">
        <div className="flex items-center gap-6 lg:gap-8 flex-shrink-0">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="font-heading text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2 group flex-shrink-0 whitespace-nowrap"
          >
            <span className="bg-[#E11D48] text-white w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black shadow-md group-hover:scale-105 transition-transform flex-shrink-0">
              LC
            </span>
            <span className="whitespace-nowrap">
              LA CAJA <span className="prism-text">LGBT</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5 xl:gap-7 flex-shrink-0">
            {link("/", t("home"), "nav-home")}
            {link("/revista", t("magazine"), "nav-revista")}
            {link("/festivales", "Festivales 🎬", "nav-festivales")}
            {link("/mi-lista", t("myList"), "nav-mylist")}
            {(user?.role === "admin" || user?.role === "editor") && link("/revista-editor", t("write"), "nav-write")}
            {user?.role === "admin" && link("/admin", t("admin"), "nav-admin")}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-shrink-0">
          {onSearch && (
            <>
              {/* Desktop Search */}
              <div className="hidden sm:flex items-center gap-2 bg-zinc-900/80 border border-white/10 rounded-lg px-3 py-1.5 focus-within:border-[#E11D48] focus-within:ring-1 focus-within:ring-[#E11D48] transition-all flex-shrink min-w-[120px] max-w-[200px]">
                <Search size={15} className="text-zinc-400 flex-shrink-0" />
                <input
                  data-testid="search-input"
                  value={search}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder={t("searchPlaceholder")}
                  className="bg-transparent text-xs sm:text-sm outline-none w-full text-white placeholder:text-zinc-500 whitespace-nowrap"
                />
                {search && (
                  <button
                    onClick={() => onSearch("")}
                    className="text-zinc-500 hover:text-white p-0.5 flex-shrink-0"
                    aria-label="Limpiar búsqueda"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>

              {/* Mobile Search Toggle */}
              <button
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className="sm:hidden p-2 text-zinc-300 hover:text-white rounded-lg bg-zinc-900/60 border border-white/10 flex-shrink-0"
                aria-label="Buscar"
              >
                <Search size={18} />
              </button>
            </>
          )}

          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <LangSwitcher />
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo nocturno"}
              className="p-2 text-zinc-300 hover:text-white rounded-lg bg-zinc-900/80 border border-white/10 hover:border-[#E11D48] transition-all flex items-center justify-center cursor-pointer flex-shrink-0"
              title={theme === "dark" ? "Modo Claro" : "Modo Nocturno"}
            >
              {theme === "dark" ? <Sun size={15} className="text-amber-400" /> : <Moon size={15} className="text-zinc-200" />}
            </button>
          </div>

          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="navbar-admin-icon"
              aria-label="Panel de Administración"
              className="hidden md:flex p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors flex-shrink-0"
            >
              <LayoutDashboard size={18} />
            </Link>
          )}

          <div className="hidden md:flex items-center gap-2.5 flex-shrink-0">
            {user ? (
              <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <span className="text-xs font-medium text-zinc-300 max-w-[120px] truncate bg-zinc-900 border border-white/10 px-3 py-1.5 rounded-lg whitespace-nowrap">
                  {user.name || user.email}
                </span>
                <button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  aria-label="Cerrar sesión"
                  className="p-2 text-zinc-400 hover:text-[#E11D48] rounded-lg hover:bg-zinc-800/60 transition-colors flex-shrink-0"
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link
                to="/auth"
                data-testid="navbar-login-button"
                className="bg-[#E11D48] text-white px-4 py-2 text-xs font-semibold rounded-lg hover:bg-[#BE123C] shadow-md shadow-[#E11D48]/20 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap flex-shrink-0 min-h-[36px]"
              >
                {t("signIn")}
              </Link>
            )}
          </div>

          <button
            className="lg:hidden p-2 text-white hover:bg-zinc-800/60 rounded-lg transition-colors flex-shrink-0"
            onClick={() => setOpen(!open)}
            aria-label="Menú principal"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Expandable Search Bar */}
      {showMobileSearch && onSearch && (
        <div className="sm:hidden mt-3 px-2">
          <div className="flex items-center gap-2 bg-zinc-900 border border-white/15 rounded-full px-4 py-2">
            <Search size={16} className="text-[#E11D48]" />
            <input
              autoFocus
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={t("searchPlaceholder")}
              className="bg-transparent text-sm outline-none w-full text-white placeholder:text-zinc-500"
            />
            {search && (
              <button onClick={() => onSearch("")} className="text-zinc-400 p-1">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Menu Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 bg-zinc-950/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-4 md:hidden border-b border-white/10 shadow-2xl">
          {link("/", t("home"), "nav-home-m")}
          {link("/revista", t("magazine"), "nav-revista-m")}
          {link("/festivales", "Festivales 🎬", "nav-festivales-m")}
          {link("/mi-lista", t("myList"), "nav-mylist-m")}
          {(user?.role === "admin" || user?.role === "editor") && link("/revista-editor", t("write"), "nav-write-m")}
          {user?.role === "admin" && link("/admin", t("admin"), "nav-admin-m")}
          
          <div className="pt-2 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-zinc-400">Tema</span>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn-m"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-900 border border-white/10 text-xs font-medium text-zinc-200"
            >
              {theme === "dark" ? (
                <>
                  <Sun size={14} className="text-amber-400" /> Modo Claro
                </>
              ) : (
                <>
                  <Moon size={14} className="text-zinc-300" /> Modo Nocturno
                </>
              )}
            </button>
          </div>

          <div className="pt-2 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-zinc-400">Idioma</span>
            <LangSwitcher />
          </div>

          <div className="pt-2 border-t border-white/10">
            {user ? (
              <button
                data-testid="logout-button-m"
                onClick={handleLogout}
                className="w-full text-left py-2 text-sm text-[#E11D48] font-medium flex items-center gap-2"
              >
                <LogOut size={16} /> {t("signOut")} ({user.name || user.email})
              </button>
            ) : (
              <Link
                to="/auth"
                data-testid="navbar-login-m"
                className="w-full text-center bg-[#E11D48] text-white py-2.5 rounded-lg text-sm font-semibold inline-block"
              >
                {t("signIn")}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

